import { useEffect, useState } from 'react'

type ConnectionInfo = {
  saveData?: boolean
  effectiveType?: string
}

type DeviceNavigator = Navigator & {
  connection?: ConnectionInfo
  deviceMemory?: number
}

export type ExperienceMode = 'full' | 'lite' | 'fallback'

function supportsWebGL() {
  try {
    const canvas = document.createElement('canvas')
    return Boolean(
      canvas.getContext('webgl2', { powerPreference: 'high-performance' }) ||
        canvas.getContext('webgl'),
    )
  } catch {
    return false
  }
}

function getMode(): ExperienceMode {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const phone = window.matchMedia('(max-width: 520px)').matches
  const nav = navigator as DeviceNavigator
  const constrainedConnection =
    nav.connection?.saveData || ['slow-2g', '2g'].includes(nav.connection?.effectiveType ?? '')

  if (reducedMotion || phone || constrainedConnection || !supportsWebGL()) return 'fallback'

  const narrow = window.matchMedia('(max-width: 800px)').matches
  const limitedMemory = typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 4
  const limitedCores = navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency <= 4
  return narrow || limitedMemory || limitedCores ? 'lite' : 'full'
}

export function useExperienceMode() {
  const [mode, setMode] = useState<ExperienceMode>(() => getMode())

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const phone = window.matchMedia('(max-width: 520px)')
    const size = window.matchMedia('(max-width: 800px)')
    const update = () => setMode(getMode())
    motion.addEventListener('change', update)
    phone.addEventListener('change', update)
    size.addEventListener('change', update)
    return () => {
      motion.removeEventListener('change', update)
      phone.removeEventListener('change', update)
      size.removeEventListener('change', update)
    }
  }, [])

  return mode
}
